import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../utils/helper";

import { useInterval } from "bx-react-ui/src/utils/hooks";
import { CircularProgress } from "../components/UI/CircularProgress/CircularProgress";
import {
  getCurrentAccount,
  getAuthorizationsLoaded,
  getNotifications,
  getNotificationsLoaded,
  getNotificationsLoading,
  getSession,
  getTexts,
  getTextsLoading,
} from "../reducers/selectors";
import { getAuthorizations } from "../services/api/authorizations";
import { getLegalContracts, setLegalContract } from "../services/api/person";
import { getMessages } from "../services/api/internalMessages";
import {
  authorizationsSuccess,
  notificationsSuccess,
  setNotificationsLoading,
  textsSet,
  textsLoadingSet,
} from "../actions/entities";
import { PopupWrapper } from "../components/PopupWrapper/PopupWrapper";
import { fetchTooltipsAndNotes } from "../services/api/entities";

import style from "./AppInfoWrapper.scss";
import { Container } from "../components/UI/Container/Container";
import { Icons } from "../components/UI/Icon/Icon";
import GenericButton from "../components/UI/Button/Button";
import downloadIcon from "../../public/svg/ButtonIcons/download.svg";

const updateAuthorizations = (dispatch) => {
  getAuthorizations()
    .then(({ response }) => {
      const newAuthorizations = response.authorizations.filter((authItem) => authItem.processedBy === "");
      dispatch(authorizationsSuccess(newAuthorizations));
    })
    .catch((err) => {
      console.error(err);
      showToast("Error obteniendo autorizaciones del servidor", true);
      dispatch(authorizationsSuccess([]));
    });
};

export const AppInfoWrapper = React.memo(({ children }) => {
  const dispatch = useDispatch();

  const authorizationsLoaded = useSelector(getAuthorizationsLoaded);
  const notificationsLoaded = useSelector(getNotificationsLoaded);
  const notificationsLoading = useSelector(getNotificationsLoading);
  const notifications = useSelector(getNotifications);

  const session = useSelector(getSession);
  const textsLoading = useSelector(getTextsLoading);
  const [contract, setContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractLoaded, setContractLoaded] = useState(false);
  const [updateContractLoading, setUpdateContractLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAbsolute, setLoadingAbsolute] = useState(true);
  const [isFirstAccess, setIsFirstAccess] = useState(false);

  useEffect(() => {
    if (notifications) {
      notifications.map((notification) => {
        if (notification.InternalMessageForm === "FirstAccess") {
          setIsFirstAccess(() => true);
          setLoadingAbsolute(() => false);
        }
      });
    }
  }, [notifications]);

  useEffect(() => {
    if (session && !textsLoading) {
      dispatch(textsLoadingSet());
      fetchTooltipsAndNotes()
        .then(({ response }) => {
          if (response.result.Code === 200) {
            dispatch(textsSet({ tooltips: response.tooltips, notes: response.notes }));
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [session, textsLoading]);

  useEffect(() => {
    if (!contractLoading && session && notifications && !contract && !contractLoaded) {
      updateLegalContract();
    }
  }, [session]);

  useEffect(() => {
    if (!notificationsLoaded && !notificationsLoading && session) {
      updateMessages();
    }
  }, [session, notificationsLoaded, notificationsLoading]);

  useEffect(() => {
    if ((notificationsLoaded && !notificationsLoading && authorizationsLoaded && session) || !session) {
      setLoadingAbsolute(() => false);
    }
  }, [session, notificationsLoaded, notificationsLoading, authorizationsLoaded]);

  const updateMessages = () => {
    dispatch(setNotificationsLoading(true));
    getMessages()
      .then(({ response }) => {
        dispatch(notificationsSuccess(response.InternalMessageSDTList));
      })
      .catch((err) => {
        console.error(err);
        showToast("Error obteniendo notificaciones del servidor", true);
        dispatch(notificationsSuccess([]));
      });
  };

  const updateLegalContract = () => {
    let stopCheckContract = false;
    notifications.map((notificationItem) => {
      if (notificationItem.InternalMessageForm === "FirstAccess") {
        stopCheckContract = true;
      }
    });

    if (!stopCheckContract) {
      setContractLoading(() => true);
      getLegalContracts()
        .then(({ response }) => {
          if (response.result.Code !== 200) {
            showToast("Error obteniendo nuevos contratos del servidor", true);
          } else {
            if (response.LegalContractSDT.LegalContractAttachmentName) {
              setContract({
                ...response.LegalContractSDT,
                LegalContractAttachmentBlob: response.blobfile,
              });
            }
            setContractLoaded(() => true);
          }
        })
        .catch((err) => {
          console.error(err);
          showToast("Error obteniendo nuevos contratos del servidor", true);
        })
        .finally(() => {
          setContractLoading(() => false);
        });
    } else {
      setContract(null);
      setContractLoading(() => false);
    }
  };

  if (loadingAbsolute)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          height: "100vh",
        }}
      >
        <Authorizations />
        <CircularProgress />
      </div>
    );

  if (isFirstAccess) {
    return <PopupWrapper>{children}</PopupWrapper>;
  }

  return (
    <React.Fragment>
      <Authorizations />
      {children}
    </React.Fragment>
  );
});

const Authorizations = () => {
  const session = useSelector(getSession);
  const authorizationsLoaded = useSelector(getAuthorizationsLoaded);
  const [authorizationsLoading, setAuthorizationsLoading] = useState(false);
  const dispatch = useDispatch();

  useInterval(() => {
    if (session) {
      updateAuthorizations(dispatch);
    }
  }, 30e4);

  useEffect(() => {
    if (!authorizationsLoaded && session && !authorizationsLoading) {
      setAuthorizationsLoading(() => true);
      updateAuthorizations(dispatch);
    }
  }, [session, authorizationsLoaded, authorizationsLoading]);

  return null;
};
